/**
 * Pick a modelled element: THE picker, for every field whose value names one
 * (issue #49).
 *
 * One component because hand-rolled pickers kept re-learning the same MUI
 * traps, and a field that stayed a text box (a transition's "Decided by") could
 * only be checked by a failed save. What varies between fields is the data -
 * which kind, which package, which rows - and that is what the props say:
 *
 *   - WHERE THE ELEMENTS COME FROM: `kind`, and the picker loads that kind's
 *     listing itself; or `rows`, when the screen already holds them (and a table
 *     of ten pickers should not make ten requests - `useElements` loads once for
 *     all of them); or `options`, lines the screen has already shaped.
 *   - WHICH OF THEM: `inPackage` keeps one package's subtree, `where` any other
 *     rule. `serverFilter` passes a narrowing the SERVER owns - `usable_as_type`
 *     is the server's own answer to "may this be a usage's type", and nothing
 *     here should re-decide what abstract means.
 *   - WHAT IS WRITTEN: `valueFor` - the identifier by default, a qualified name
 *     for a package, a reference as written for a transition end.
 *   - QUICK VALIDATION: `strict` flags a value that is not one of the elements
 *     offered, before a save is refused for it. Only where the picker can know:
 *     a server search sees one page, so it never flags.
 *
 * Three things are load-bearing, each found the hard way in a picker this
 * replaced:
 *
 *   - THE CURRENT VALUE IS ALWAYS AN OPTION. Autocomplete treats a value it
 *     cannot find as invalid, warns, and renders a blank box - so a field that
 *     had a value looked unset, and was cleared on the next click. A value the
 *     list does not hold is shown as written.
 *   - THE TEXT BOX IS NOT CONTROLLED. A controlled inputValue starting empty
 *     blanks the box even with a value set. Typing is observed, not owned.
 *   - SLOTPROPS ARE MERGED, NEVER REPLACED. htmlInput carries the ref for the
 *     real <input>; replacing the object leaves MUI unable to find its field.
 */

import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import type { SxProps, Theme } from '@mui/material/styles'
import { api } from '../../api'
import type { EntityRow, Schema } from '../../schema'
import { MONOSPACE } from '../../theme'
import { elementOption, narrow, pluralOf, unlisted, useElements } from './elements'
import type { PickerOption } from './elements'

export type { PickerOption } from './elements'

/** How long to wait after the last keystroke before asking the server. */
const DEBOUNCE_MS = 250

/**
 * One page of a server search, for a kind too large or too richly searched to
 * filter here - requirements match on their statement text, which a listing
 * row does not carry.
 */
function useServerSearch(
  schema: Schema | undefined,
  kind: string | undefined,
  enabled: boolean,
  serverFilter: Record<string, string>,
) {
  const plural = pluralOf(schema, kind)
  const filterKey = JSON.stringify(serverFilter)
  const [query, setQuery] = useState('')
  const [rows, setRows] = useState<EntityRow[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!enabled || !plural) return
    let live = true
    setLoading(true)
    const timer = setTimeout(() => {
      api
        .list(plural, { search: query || undefined, limit: 50, ...JSON.parse(filterKey) })
        .then((page) => live && setRows(page.items))
        .catch(() => live && setRows([]))
        .finally(() => live && setLoading(false))
    }, DEBOUNCE_MS)
    return () => {
      live = false
      clearTimeout(timer)
    }
  }, [enabled, plural, query, filterKey])

  return { rows, loading, setQuery }
}

interface Common {
  /** Resolves `kind` to its listing. Without one, `kind` is taken as the plural. */
  schema?: Schema
  label?: string
  /** The accessible name, when there is no visible label (a table cell). */
  ariaLabel?: string

  /** The element KIND offered, by name or plural. The picker loads it. */
  kind?: string
  /** Or the rows themselves, when the screen already has them. */
  rows?: EntityRow[]
  /** Only elements inside this package, at any depth, by qualified name. */
  inPackage?: string
  /** Only elements this says yes to. */
  where?: (row: EntityRow) => boolean
  /** Narrowing the server does, as query parameters of the kind's listing. */
  serverFilter?: Record<string, string>
  /**
   * 'local' filters a loaded listing as you type; 'server' asks the server on
   * each keystroke, one page at a time. Only with `kind`.
   */
  search?: 'local' | 'server'

  /** What is written for a row. The identifier by default. */
  valueFor?: (row: EntityRow) => string
  /** How a row reads in the list. `elementOption` by default. */
  describe?: (row: EntityRow, value: string) => PickerOption
  /**
   * Lines given ready-made, listed before any rows: a list the screen has
   * already shaped (a package tree, a transition end's spelling), or a choice
   * that is not an element at all ("New package…").
   */
  options?: PickerOption[]
  /** What the box shows once chosen: what is written, or the label. */
  show?: 'value' | 'label'

  /**
   * Flag a value that is not one of the elements offered: `true`, or the noun
   * to say it with - "'x' is not a part in the model".
   */
  strict?: boolean | string

  error?: boolean
  helperText?: ReactNode
  required?: boolean
  disabled?: boolean
  placeholder?: string
  variant?: 'outlined' | 'standard'
  size?: 'small' | 'medium'
  fullWidth?: boolean
  /** Monospace text, for a list of identifiers or qualified names. */
  monospace?: boolean
  sx?: SxProps<Theme>
}

export type ElementPickerProps = Common &
  (
    | { multiple?: false; value: string | null; onChange: (value: string) => void }
    | { multiple: true; value: string[]; onChange: (value: string[]) => void }
  )

export default function ElementPicker(props: ElementPickerProps) {
  const {
    schema, label, ariaLabel, kind, rows: given, inPackage, where, serverFilter = {},
    search = 'local', valueFor = (row) => row.id, describe = elementOption, options: fixed = [],
    show = 'value', strict = false, error, helperText, required, disabled, placeholder,
    variant, size = 'small', fullWidth, monospace = false, sx,
  } = props

  const onServer = search === 'server' && !given && Boolean(kind)
  const loaded = useElements(schema, given || onServer ? undefined : kind, serverFilter)
  const searched = useServerSearch(schema, kind, onServer, serverFilter)

  const values = props.multiple ? props.value : props.value ? [props.value] : []

  // Not memoized: `where`, `valueFor` and `describe` are usually inline arrows,
  // so a memo would either recompute every render anyway or go stale. A few
  // thousand rows filter in well under a frame.
  const source = given ?? (onServer ? searched.rows : loaded.rows)
  const listed = [
    ...fixed,
    ...narrow(source, { inPackage, where }).map((row) => describe(row, valueFor(row))),
  ]

  // A server page, or a listing the server cut short, cannot say a value is
  // missing - only that it was not in what was sent.
  const canJudge = given ? true : !onServer && loaded.complete
  const missing = strict && canJudge ? unlisted(values, listed) : []
  const noun = typeof strict === 'string' ? strict : 'element'
  const complaint = missing.length
    ? `${missing.map((value) => `'${value}'`).join(', ')} ${missing.length === 1 ? 'is' : 'are'} not ${noun === 'element' ? 'an element' : `a ${noun}`} in the model`
    : ''

  // The current value first, as written, when the list does not hold it.
  const offered = new Set(listed.map((option) => option.value))
  const options: PickerOption[] = [
    ...values.filter((value) => !offered.has(value)).map((value) => ({ value })),
    ...listed,
  ]

  const find = (value: string) => options.find((option) => option.value === value) ?? { value }
  const selected = props.multiple ? values.map(find) : values.length ? find(values[0]) : null
  const loading = onServer ? searched.loading : loaded.loading

  const boxText = (option: PickerOption) =>
    option.chosen ?? (show === 'label' ? option.label ?? option.value : option.value)

  return (
    <Autocomplete<PickerOption, boolean>
      multiple={props.multiple}
      disabled={disabled}
      options={options}
      value={selected as never}
      onChange={(_event, next) => {
        if (props.multiple) props.onChange((next as PickerOption[]).map((option) => option.value))
        else props.onChange((next as PickerOption | null)?.value ?? '')
      }}
      // Observed, not controlled: only a keystroke drives a server search,
      // and 'reset' fires when a value is chosen.
      onInputChange={(_event, next, reason) => {
        if (onServer && reason === 'input') searched.setQuery(next)
      }}
      getOptionLabel={boxText}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      // The server already filtered, and filtering again would hide the
      // current value whenever it does not match what has been typed. Locally,
      // every line of the option is matched, not only what the box shows.
      filterOptions={onServer ? (all) => all : (all, state) => {
        const typed = state.inputValue.trim().toLowerCase()
        if (!typed || all.some((option) => boxText(option) === state.inputValue)) return all
        return all.filter((option) =>
          [option.value, option.label, option.code, option.detail, option.note]
            .some((text) => text?.toLowerCase().includes(typed)))
      }}
      loading={loading}
      size={size}
      fullWidth={fullWidth}
      clearOnBlur={false}
      sx={sx}
      // The list is at least wide enough to read a qualified name, whatever
      // the box's width: in a table cell the box is narrow and the lines are not.
      slotProps={{ popper: { placement: 'bottom-start', sx: { minWidth: 360 } } }}
      renderOption={({ key, ...optionProps }, option) => (
        <Box component="li" key={key} {...optionProps}>
          <OptionLine option={option} monospace={monospace} />
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant={variant}
          required={required}
          placeholder={placeholder}
          error={Boolean(error) || missing.length > 0}
          helperText={complaint || helperText}
          slotProps={{
            ...params.slotProps,
            htmlInput: {
              ...params.slotProps.htmlInput,
              ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
            },
            input: {
              ...params.slotProps.input,
              ...(monospace ? { sx: { fontFamily: MONOSPACE } } : {}),
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={16} /> : null}
                  {params.slotProps.input.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  )
}

function OptionLine({ option, monospace }: { option: PickerOption; monospace: boolean }) {
  const depth = option.depth ?? 0
  return (
    <Box sx={{ minWidth: 0, pl: depth * 2 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Typography
          variant="body2"
          sx={monospace ? { fontFamily: MONOSPACE, fontSize: '0.85rem' } : undefined}
        >
          {depth > 0 && (
            <Box component="span" sx={{ color: 'text.disabled' }}>
              {'→ '}
            </Box>
          )}
          {option.label ?? option.value}
        </Typography>
        {option.code && (
          <Typography variant="caption" sx={{ fontFamily: MONOSPACE }}>
            {option.code}
          </Typography>
        )}
        {option.tag && <Chip label={option.tag} size="small" variant="outlined" color="warning" />}
      </Stack>
      {option.detail && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontFamily: MONOSPACE, display: 'block' }}
        >
          {option.detail}
        </Typography>
      )}
      {option.note && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {option.note}
        </Typography>
      )}
    </Box>
  )
}
